import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table/interface';
import dayjs from 'dayjs';

import BouncedEmailsContext from '@totem/components/administration/bouncedEmails/BouncedEmailsContext';
import BouncedEmailStatusTag from '@totem/components/administration/bouncedEmails/BouncedEmailStatusTag';
import { BouncedEmail } from '@totem/components/administration/bouncedEmails/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

const uniqueOrganizations = (record: BouncedEmail) =>
  Array.from(
    new Set((record.contacts ?? []).map((contact) => contact.organizationName)),
  ).sort();

const BouncedEmailsTable = () => {
  const { data, loading, totalRecords } = useContext(BouncedEmailsContext);
  const { input, handleTableChange, getFilterOptions } =
    useContext(InputContext);

  const columns: ColumnsType<BouncedEmail> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      render: (email: string) => (
        <Link
          to={`/dashboard/admin/bouncedEmails/${encodeURIComponent(email)}`}
        >
          {email}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      sorter: true,
      filters: getFilterOptions('status'),
      filterMultiple: true,
      render: (_, record) => <BouncedEmailStatusTag status={record.status} />,
    },
    {
      title: 'Organizations',
      key: 'organization',
      filters: getFilterOptions('organization'),
      filterMultiple: true,
      filterSearch: true,
      render: (_, record) => uniqueOrganizations(record).join(', ') || '—',
    },
    {
      title: 'Contacts',
      key: 'contacts',
      width: 100,
      align: 'right',
      render: (_, record) => record.contacts?.length ?? 0,
    },
    {
      title: 'Bounces',
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'right',
      sorter: true,
    },
    {
      title: 'First Bounce',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      sorter: true,
      render: (value: string) =>
        value ? dayjs(value).format(DATE_TIME_FORMAT) : '—',
    },
    {
      title: 'Last Bounce',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      sorter: true,
      render: (value: string) =>
        value ? dayjs(value).format(DATE_TIME_FORMAT) : '—',
    },
  ];

  return (
    <IBTable<BouncedEmail>
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey="email"
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
    />
  );
};

export default BouncedEmailsTable;
